import React, { useMemo } from 'react'

import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { selectProcessDocumentContact } from '../../../store/processDocument/documentUsers'
import { useAppSelector } from '../../../store/hooks'
import {
  selectDocumentLabel,
  selectDocumentSummaryExecutionLoading,
  selectDocumentSummaryResult,
  selectDocumentType,
  selectDocumentTypeCalculationLoading,
} from '../../../store/processDocument/documentSummary'
import { selectProcessDocumentFileInfo } from '../../../store/processDocument/documentInfo'

import { FALLBACK_DOCUMENT_TYPE_ID } from '../../../models/DocumentType'
import { ProcessDocumentFileUploadProgress, ProgressItem } from '../ProcessDocumentFileUploadProgress/ProcessDocumentFileUploadProgress'
import '../DocumentSummary/DocumentSummary.scss'
import { useAttachmentSummaryProcessingOrchestration } from './hooks'
import { selectProcessEmailAttachmentIsAttachmentLoading } from '../../../store/processDocument/emailAttachmentInfo'
import { AttachmentSummaryTrackingContext } from './AttachmentSummaryPage'

export interface AttachmentSummaryProcessingProps {
  trackingEventContext: AttachmentSummaryTrackingContext
}

export const AttachmentSummaryProcessing: React.FC<AttachmentSummaryProcessingProps> = ({ trackingEventContext }) => {
  const contact = useAppSelector(selectProcessDocumentContact)
  useAttachmentSummaryProcessingOrchestration(trackingEventContext)

  if (!contact) {
    return null
  }

  return (
    <div className='h-100 w-100 d-flex align-items-center justify-content-center'>
      <div className='d-flex flex-column align-items-center zocks-gap-2 '>
        <div className='d-flex flex-column align-items-center zocks-gap-0.5 document-summary-file-upload-header'>
          <h3>Turn Attachment into Insights</h3>
          <span className='paragraph-medium text-center'>
            Processing email attachment for {getUserDisplayName(contact)}. Your AI Assistant is extracting key insights and preparing updates for your CRM and
            financial systems.
          </span>
        </div>
        <AttachmentSummaryProcessingProgress />
      </div>
    </div>
  )
}

const AttachmentSummaryProcessingProgress = () => {
  const isAttachmentLoading = useAppSelector(selectProcessEmailAttachmentIsAttachmentLoading)
  const fileInfo = useAppSelector(selectProcessDocumentFileInfo)

  const documentTypeCalculationLoading = useAppSelector(selectDocumentTypeCalculationLoading)
  const documentType = useAppSelector(selectDocumentType)
  const documentLabel = useAppSelector(selectDocumentLabel)

  const documentSummaryResult = useAppSelector(selectDocumentSummaryResult)
  const documentSummaryExecutionLoading = useAppSelector(selectDocumentSummaryExecutionLoading)

  const progressItems: ProgressItem[] = useMemo(() => {
    return [
      {
        isLoading: isAttachmentLoading,
        isSuccess: !isAttachmentLoading && !!fileInfo,
        label: fileInfo ? `Loaded ${fileInfo.fileName}` : `Loading attachment…`,
      },
      {
        isLoading: documentTypeCalculationLoading,
        isSuccess: !documentTypeCalculationLoading && !!documentType,
        label: documentType ? (
          documentType.systemDocumentTypeId === FALLBACK_DOCUMENT_TYPE_ID ? (
            <>
              Couldn't match document type → Using <strong>{documentType.name}</strong> as fallback
            </>
          ) : (
            <>
              Identified as <strong>{documentLabel}</strong> → Applying <strong>{documentType.name}</strong> template
            </>
          )
        ) : (
          'Analyzing document type…'
        ),
      },
      {
        isLoading: documentSummaryExecutionLoading,
        isSuccess: !documentSummaryExecutionLoading && !!documentSummaryResult,
        label: documentSummaryResult ? 'Summary ready to review and sync' : 'Extracting insights and creating summary…',
      },
    ]
  }, [documentLabel, documentSummaryExecutionLoading, documentSummaryResult, documentType, documentTypeCalculationLoading, fileInfo, isAttachmentLoading])

  return <ProcessDocumentFileUploadProgress items={progressItems} />
}
